import React, { useEffect } from "react";
import { Header } from "../../components/Header";
import { Footer } from "../../components/Footer";
import { injectJsonLd, breadcrumbSchema } from "../../seo";

const languages = [
  {
    lang: "English",
    native: "English",
    flag: "🇬🇧",
    example: "How do I track my order?",
  },
  {
    lang: "Bahasa Melayu",
    native: "Melayu",
    flag: "🇲🇾",
    example: "Bagaimana saya nak jejak pesanan saya?",
  },
  {
    lang: "Mandarin Chinese",
    native: "中文",
    flag: "🇨🇳",
    example: "我怎么追踪我的订单？",
  },
];

const highlights = [
  {
    title: "Auto Language Detection",
    description:
      "The AI detects the language of each incoming message and responds in kind — no manual switching, no configuration per user.",
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 21l5.25-11.25L21 21m-9-3h7.5M3 5.621a48.474 48.474 0 016-.371m0 0c1.12 0 2.233.038 3.334.114M9 5.25V3m3.334 2.364C11.176 10.658 7.69 15.08 3 17.502m9.334-12.138c.896.061 1.785.147 2.666.257m-4.589 8.495a18.023 18.023 0 01-3.827-5.802" />
      </svg>
    ),
  },
  {
    title: "Consistent Accuracy Across Languages",
    description:
      "Your FAQ knowledge base is answered with the same accuracy in English, BM, and Mandarin. The AI doesn't lose context when switching languages.",
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
  },
  {
    title: "Malaysian Market Focus",
    description:
      "Built from the ground up for Malaysia's multilingual market. Your customers expect to be served in their preferred language — Onyx AI delivers that automatically.",
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z" />
        <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z" />
      </svg>
    ),
  },
  {
    title: "Language Switcher UI",
    description:
      "The landing page and chat interface support EN, 中文, and BM. Visitors can toggle their preferred language from the navigation header.",
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M3 7.5L7.5 3m0 0L12 7.5M7.5 3v13.5m13.5 0L16.5 21m0 0L12 16.5m4.5 4.5V7.5" />
      </svg>
    ),
  },
];

export function MultilingualPage() {
  useEffect(() => {
    document.title = "Multilingual AI Chatbot — Onyx AI";
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute(
        "content",
        "Onyx AI serves customers in English, Bahasa Melayu, and 中文 automatically. Built for Malaysia's multilingual market — no configuration needed."
      );
    }
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.setAttribute("href", "https://onyxai.chat/features/multilingual");
    }
    return injectJsonLd("breadcrumb-jsonld", breadcrumbSchema([
      { name: "Home", path: "/" },
      { name: "Features", path: "/features" },
      { name: "Multilingual Support", path: "/features/multilingual" },
    ]));
  }, []);

  return (
    <div className="min-h-screen bg-[#0a0a12]">
      <Header />
      <main className="pt-32 pb-24 px-6">
        <div className="max-w-5xl mx-auto">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-sm text-[#6a5a7a] mb-10">
            <a href="/" className="hover:text-[#a078d0] transition-colors">Home</a>
            <span>/</span>
            <a href="/features" className="hover:text-[#a078d0] transition-colors">Features</a>
            <span>/</span>
            <span className="text-[#8a7a9a]">Multilingual Support</span>
          </nav>

          {/* Hero */}
          <div className="mb-16">
            <p className="text-[#a078d0] text-sm font-medium uppercase tracking-widest mb-4">
              Multilingual Support
            </p>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-5">
              Serve Every Customer in Their Language
            </h1>
            <p className="text-[#8a7a9a] text-lg max-w-2xl">
              Malaysia is trilingual by default. Onyx AI automatically detects and replies in English, Bahasa Melayu, or 中文 — so every customer feels at home.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <a
                href="/signup"
                className="inline-block px-7 py-3 bg-[#7b5ea7] hover:bg-[#6a4f96] text-white font-medium rounded-lg transition-colors"
              >
                Get Started Free
              </a>
              <a
                href="/features"
                className="inline-block px-7 py-3 border border-[#2a2040] hover:border-[#a078d0] text-[#8a7a9a] hover:text-white font-medium rounded-lg transition-colors"
              >
                All Features
              </a>
            </div>
          </div>

          {/* Language cards */}
          <div className="grid md:grid-cols-3 gap-5 mb-16">
            {languages.map((l) => (
              <div key={l.lang} className="p-6 rounded-xl onyx-card text-center">
                <div className="text-4xl mb-3">{l.flag}</div>
                <p className="text-white font-semibold mb-1">{l.native}</p>
                <p className="text-[#6a5a7a] text-xs mb-4">{l.lang}</p>
                <div className="bg-[#14121e] border border-[#2a2040] rounded-lg px-4 py-3 text-[#8a7a9a] text-sm italic">
                  "{l.example}"
                </div>
              </div>
            ))}
          </div>

          {/* Highlights */}
          <div className="grid md:grid-cols-2 gap-6">
            {highlights.map((item) => (
              <div key={item.title} className="p-6 rounded-xl onyx-card onyx-glow-hover">
                <div className="w-10 h-10 rounded-lg bg-[#a078d0]/10 flex items-center justify-center text-[#c0a0e0] mb-4">
                  {item.icon}
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">{item.title}</h3>
                <p className="text-sm text-[#8a7a9a] leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
